// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.
import { CustomEvent } from "../../Utils/CustomEvents.js";
import { TvChartSupportedResolutionArray } from "./TradingViewPrimitives/TvEnums";
import { IDatafeedChartApi, LibrarySymbolInfo, ResolveCallback, SearchSymbolResultItem, SymbolResolveExtension, ErrorCallback } from "../../Libs/TradingViewChart/charting_library_cloned_data/charting_library/charting_library";
import { DataCache } from "../DataCache.js";
import { TvSymbolConvertor } from "./Convertors/TvSymbolConvertor";
import { TvHistoryManager } from "./Managers/TvHistoryManager";
import { TvSearchSymbolManager } from "./Managers/TvSearchSymbolManager";
import { TvSymbol } from "./TradingViewPrimitives/TvSymbol.js";
import { Instrument } from "../cache/Instrument.js";

export class TvDataFeed implements IDatafeedChartApi
{
    public onReadyEvent = new CustomEvent();
    private symbolConvertor = new TvSymbolConvertor();
    private historyManager = new TvHistoryManager();
    private searchSymbolManager: TvSearchSymbolManager = new TvSearchSymbolManager();

    //#region TradingView DataFeed interface implementation
    onReady(callback)
    {
        setTimeout(() =>
        {
            callback({ supported_resolutions: TvChartSupportedResolutionArray, });
            this.onReadyEvent.Raise();
        }, 0);
    }

    async searchSymbols(userInput: string, exchange: string, symbolType: string, onResultReadyCallback) 
    {
        const filteredList = await this.searchSymbolManager.searchSymbols(userInput, exchange, symbolType);
        const result: SearchSymbolResultItem[] = [];
        for (const instrument of filteredList) {
            const resultItem = this.symbolConvertor.convertToTvSearchSymbolResultItem(instrument);
            result.push(resultItem);
        }
        onResultReadyCallback(result);
    }

    public resolveSymbol(symbolName: string, onResolve: ResolveCallback, onError: ErrorCallback, extension: SymbolResolveExtension): void
    {
        try {
            setTimeout(() => 
            {
                if (this.symbolConvertor.isFakeSymbolName(symbolName)) //кейс, когда резолвится скрытый инпут индикатора
                {
                    const fakeInfo: LibrarySymbolInfo = this.symbolConvertor.getFakeSymbol();
                    onResolve(fakeInfo);
                    return;
                }

                const instrument = DataCache.getInstrumentByName(symbolName);
                if (!instrument) {
                    onError(`[TvDataFeed::resolveSymbol]: Instrument ${symbolName} not found`);
                    return;
                }
                const symbolInfo = this.symbolConvertor.convertToTvSymbol(instrument, extension);
                onResolve(symbolInfo);
            }, 0);  // Datafeed.resolveSymbol must be async: https://www.tradingview.com/charting-library-docs/latest/connecting_data/Datafeed-API/#asynchronous-callbacks
        }
        catch (err) { onError(err.message); }
    }

    async getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback)
    {
        await this.historyManager.getBars(symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback);
    }

    subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback)
    {
        this.historyManager.subscribeBars(symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback);
    }

    unsubscribeBars(subscriberUID)
    {
        this.historyManager.unsubscribeBars(subscriberUID);
    }
    //#endregion TradingView DataFeed interface implementation

    setAccount(account)
    {
        this.historyManager.setAccount(account);
    }

    convertResolutionToPeriod(resolution)
    {
        return this.historyManager.convertResolutionToPeriod(resolution);
    }

    convertPeriodToResolution(period)
    {
        return this.historyManager.convertPeriodToResolution(period);
    }

    public getSymbolByName(nameWithExchange: string): Instrument | null
    {
        return this.searchSymbolManager.getSymbolByName(nameWithExchange);
    }

    dispose()
    {
        this.searchSymbolManager.dispose();
        this.historyManager.dispose();
    }
}